import { Control } from "@evergis/sgis";
import { PointFeature } from "@evergis/sgis/es/features/PointFeature";
import { Map } from "@evergis/sgis/es/Map";
import { LabelFeature } from "@evergis/sgis/es/features/Label";
import { FeatureLayer } from "@evergis/sgis/es/layers/FeatureLayer";
import { SnappingProviderSettings } from "../../ducks/snapping/types";
import { createSnappingSymbol } from "../symbols/editorSymbols/createSnappingSymbol";
import { editorPolygonSnapSymbol } from "../symbols/editorSymbols/ShadowedPolySymbol";
import { DEFAULT_LABEL_POSITION } from "../utils";
import { ServerSnappingProvider } from "./ServerSnappingProvider";
import { SnappingDataReceiveEvent } from "./SnappingDataReceiveEvent";
import { tooltipSymbol } from "./TooltipSymbol";

const LAYER_NAME_MAX_LENGTH = 32;
const TEXT_ELLIPSIS = "...";

export type ServerSnappingControllerConfig = {
  map: Map;
  snappingProvider: ServerSnappingProvider;
  snappingFeature: PointFeature;
  control: Control;
  snappingProviderSettings?: SnappingProviderSettings;
  tempLabel?: LabelFeature;
};

export class ServerSnappingController {
  private snappingProviderSettings: SnappingProviderSettings;

  private snappingProvider: ServerSnappingProvider;

  private snappingFeature: PointFeature;

  private tempLayer: FeatureLayer;

  private map: Map;

  private control: Control;

  private tempLabel: LabelFeature;

  private useNewLabel: boolean;

  constructor({
    map,
    snappingProvider,
    snappingProviderSettings,
    snappingFeature,
    tempLabel,
    control,
  }: ServerSnappingControllerConfig) {
    this.map = map;
    this.snappingFeature = snappingFeature;
    this.snappingProvider = snappingProvider;
    this.snappingProviderSettings = snappingProviderSettings;
    this.tempLabel = tempLabel;
    this.control = control;

    this.snappingProvider.on(SnappingDataReceiveEvent.type, this.drawLayerName.bind(this));
  }

  updateSnappingProvider(snappingProviderSettings: Partial<SnappingProviderSettings>) {
    if (snappingProviderSettings?.enabled !== undefined) {
      this.snappingProvider.setEnabled(snappingProviderSettings?.enabled);
    }

    if (snappingProviderSettings?.layerFeaturesSnapping !== undefined) {
      this.snappingProvider.setLayerFeaturesSnappingEnabled(snappingProviderSettings?.layerFeaturesSnapping);
    }

    if (snappingProviderSettings?.snappingDistance) {
      this.snappingProvider.setSnappingDistance(snappingProviderSettings?.snappingDistance);

      this.snappingFeature.symbol = createSnappingSymbol(snappingProviderSettings?.snappingDistance);
    }

    if (snappingProviderSettings?.showSnappingPoint !== undefined) {
      this.snappingFeature.symbol = snappingProviderSettings?.showSnappingPoint
        ? createSnappingSymbol(snappingProviderSettings?.snappingDistance)
        : editorPolygonSnapSymbol;
    }

    this.snappingProviderSettings = {
      ...this.snappingProviderSettings,
      ...snappingProviderSettings,
    };
  }

  setSnappingLayer(layer: FeatureLayer) {
    this.snappingProvider.setLayer(layer);
  }

  activate() {
    if (!this.tempLabel) {
      this.tempLabel = new LabelFeature(DEFAULT_LABEL_POSITION, {
        crs: this.map.crs,
        symbol: tooltipSymbol,
      });
      this.tempLayer = new FeatureLayer();

      this.map.addLayer(this.tempLayer);
      this.tempLayer.add(this.tempLabel);

      this.useNewLabel = true;
    }
  }

  deactivate() {
    if (this.useNewLabel) {
      if (this.tempLayer.has(this.tempLabel)) {
        this.tempLayer.remove(this.tempLabel);
      }

      delete this.tempLabel;

      this.map.removeLayer(this.tempLayer);

      delete this.tempLayer;
    }
  }

  drawLayerName({ snappingData }: SnappingDataReceiveEvent) {
    if (this.tempLabel && this.snappingProviderSettings?.showLayerName) {
      if (snappingData) {
        const {
          point,
          snappingResult: { layerName },
        } = snappingData;

        if (layerName?.length > LAYER_NAME_MAX_LENGTH) {
          this.tempLabel.content = layerName.slice(0, LAYER_NAME_MAX_LENGTH) + TEXT_ELLIPSIS;
        } else {
          this.tempLabel.content = layerName;
        }

        this.tempLabel.position = point;

        if (this.useNewLabel) {
          this.tempLabel.show();
          this.tempLayer.redraw();
        }
      } else if (this.useNewLabel) {
        this.tempLabel.hide();
        this.tempLayer.redraw();
      }
    }
  }
}
